import type { Salarie } from '@shared/types/Salarie';
import { roundFCFA } from '@shared/utils/round';
import { INDEMNITE_PRECARITE_CDD_OPTION } from '@shared/constants/Taux';
import { ancienneteAnneesFractionnaires, preavisLegalParAnciennete } from './PreavisLegal';

/**
 * Calcul des indemnités de fin de contrat (Code du travail, loi 022/2021 ; la loi 3/94 est abrogée,
 * art. 413).
 */
export class IndemnitesFinContratService {
  /**
   * Indemnité de licenciement = 20 % du salaire mensuel moyen × ancienneté (années, fractions
   * comprises), sans ancienneté minimale : Code du travail art. 87 et 90 (minimum obligatoire).
   */
  static calculerIndemniteLicenciement(salaire_mensuel: number, ancienneteAnnees: number): number {
    return roundFCFA(salaire_mensuel * 0.2 * ancienneteAnnees);
  }

  /**
   * Préavis légal selon l'ANCIENNETÉ (Code du travail art. 82) : 15 jours jusqu'à 1 an, 1 mois de
   * 1 à 3 ans, 2 mois de 3 à 5 ans, 3 mois de 5 à 10 ans, 4 mois de 10 à 15 ans, 5 mois de 15 à
   * 20 ans, 6 mois de 20 à 30 ans, majoration de 10 jours par année au-delà. Indépendant de la
   * catégorie. Minimum légal : une convention peut être plus favorable.
   * Ancienneté mesurée jusqu'à `date_sortie`, à défaut jusqu'à aujourd'hui.
   */
  static dureePreavis(salarie: Pick<Salarie, 'date_embauche' | 'date_sortie'>): {
    duree: number;
    unite: 'jour' | 'mois';
  } {
    const p = preavisLegalParAnciennete(
      ancienneteAnneesFractionnaires(salarie.date_embauche, salarie.date_sortie),
    );
    return { duree: p.duree, unite: p.unite };
  }

  /**
   * Indemnité de précarité CDD : AUCUNE par défaut (Code du travail art. 60 : le CDD prend fin
   * « sans indemnité de rupture, à l'exception de l'indemnité des congés payés »). Option
   * paramétrable, à n'activer que si une convention ou le contrat la prévoit.
   */
  static calculerIndemnitePrecaritéCDD(
    brut_total: number,
    option: { actif: boolean; taux: number } = {
      actif: false,
      taux: INDEMNITE_PRECARITE_CDD_OPTION,
    },
  ): number {
    if (!option.actif) return 0;
    return roundFCFA(brut_total * option.taux);
  }

  /** Calcul de l'ancienneté en années (avec décimale). */
  static calculerAnciennete(date_embauche: string, date_sortie?: string): number {
    const debut = new Date(date_embauche);
    const fin = date_sortie ? new Date(date_sortie) : new Date();
    return Math.max(0, (fin.getTime() - debut.getTime()) / (365.25 * 24 * 3600 * 1000));
  }
}
